import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Heart, ShoppingBag, Menu, X } from "lucide-react";
import { useStore } from "@/context/StoreContext";
import { fetchCollections } from "@/lib/shopify";

// Legacy pretty routes preserved so old URLs like /dresses keep working.
const LEGACY_PATHS = {
  dresses: "/dresses",
  "new-arrivals": "/new-arrivals",
  sale: "/sale",
};
const linkForHandle = (handle) => LEGACY_PATHS[handle] || `/collection/${handle}`;

const FALLBACK_LINKS = [
  { to: "/shop", label: "Shop" },
  { to: "/dresses", label: "Dresses" },
  { to: "/new-arrivals", label: "New Arrivals" },
  { to: "/sale", label: "Sale" },
];

const Navbar = ({ transparentHero = false }) => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [links, setLinks] = useState(FALLBACK_LINKS);
  const { cartCount, setCartOpen, wishlist } = useStore();
  const { pathname } = useLocation();

  useEffect(() => {
    let alive = true;
    fetchCollections()
      .then((cols) => {
        if (!alive || !cols || cols.length === 0) return;
        setLinks([
          { to: "/shop", label: "Shop" },
          ...cols.map((c) => ({ to: linkForHandle(c.handle), label: c.title })),
        ]);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setMobileOpen(false); }, [pathname]);

  const solid = !transparentHero || scrolled;

  return (
    <>
      <nav
        data-testid="main-navbar"
        className={`fixed top-0 left-0 w-full z-40 transition-all duration-300 ${
          solid
            ? "bg-[color:var(--vw-bg)] shadow-[0_1px_20px_-6px_rgba(74,43,45,0.15)]"
            : "bg-transparent"
        }`}
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 h-16 md:h-20 flex items-center justify-between">
          {/* Left — Logo */}
          <Link
            to="/"
            data-testid="logo-link"
            className={`font-serif-display text-2xl md:text-3xl tracking-wide transition-colors duration-300 ${
              solid ? "text-[color:var(--vw-ink)]" : "text-white"
            }`}
          >
            Vastra West
          </Link>

          {/* Right — Nav (desktop) */}
          <div className={`hidden md:flex items-center gap-8 transition-colors duration-300 ${solid ? "text-[color:var(--vw-ink)]" : "text-white"}`}>
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.to.replace(/^\//, "").replace(/\//g, "-")}`}
                className={({ isActive }) =>
                  `overline transition-opacity hover:opacity-60 ${isActive ? "opacity-100" : "opacity-90"}`
                }
              >
                {l.label}
              </NavLink>
            ))}

            {/* Wishlist */}
            <Link
              to="/wishlist"
              data-testid="wishlist-link"
              className="overline hover:opacity-60 transition-opacity flex items-center gap-1.5"
            >
              <Heart size={14} strokeWidth={1.5} />
              <span>Wishlist{wishlist.length > 0 ? ` (${wishlist.length})` : ""}</span>
            </Link>

            {/* Cart */}
            <button
              data-testid="open-cart-btn"
              onClick={() => setCartOpen(true)}
              className="overline hover:opacity-60 transition-opacity flex items-center gap-1.5"
            >
              <ShoppingBag size={14} strokeWidth={1.5} />
              <span>Cart{cartCount > 0 ? ` (${cartCount})` : ""}</span>
            </button>
          </div>

          {/* Right — Mobile icons + menu */}
          <div className={`md:hidden flex items-center gap-5 ${solid ? "text-[color:var(--vw-ink)]" : "text-white"}`}>
            <Link to="/wishlist" data-testid="wishlist-link-mobile" aria-label="Wishlist" className="relative">
              <Heart size={20} strokeWidth={1.5} />
              {wishlist.length > 0 && (
                <span data-testid="wishlist-count" className="absolute -top-2 -right-2 text-[10px] font-utility w-4 h-4 flex items-center justify-center rounded-full bg-[color:var(--vw-ink)] text-[color:var(--vw-bg)]">
                  {wishlist.length}
                </span>
              )}
            </Link>
            <button
              data-testid="open-cart-btn-mobile"
              aria-label="Open cart"
              onClick={() => setCartOpen(true)}
              className="relative"
            >
              <ShoppingBag size={20} strokeWidth={1.5} />
              {cartCount > 0 && (
                <span data-testid="cart-count" className="absolute -top-2 -right-2 text-[10px] font-utility w-4 h-4 flex items-center justify-center rounded-full bg-[color:var(--vw-ink)] text-[color:var(--vw-bg)]">
                  {cartCount}
                </span>
              )}
            </button>
            <button data-testid="mobile-menu-toggle" aria-label="Open menu" onClick={() => setMobileOpen(true)}>
              <Menu size={22} />
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div data-testid="mobile-nav-drawer" className="fixed inset-0 z-50 bg-[color:var(--vw-bg)] md:hidden animate-fade-up">
          <div className="flex items-center justify-between px-6 h-16">
            <span className="font-serif-display text-2xl">Vastra West</span>
            <button data-testid="close-mobile-nav" aria-label="Close menu" onClick={() => setMobileOpen(false)}>
              <X size={22} />
            </button>
          </div>
          <div className="flex flex-col p-8 gap-6">
            {links.map((l) => (
              <Link key={l.to} to={l.to} className="font-serif-display text-3xl text-[color:var(--vw-ink)]">
                {l.label}
              </Link>
            ))}
            <Link to="/wishlist" className="font-serif-display text-3xl text-[color:var(--vw-ink)]">Wishlist</Link>
            <button onClick={() => { setMobileOpen(false); setCartOpen(true); }} className="font-serif-display text-3xl text-[color:var(--vw-ink)] text-left">Cart</button>
            <div className="pt-6 flex flex-col gap-4 overline">
              <Link to="/about">About</Link>
              <Link to="/contact">Contact</Link>
              <Link to="/faq">FAQ</Link>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;
